// @safetyhub/report-composer — 편집형 list 데이터 소스 → 작성폼 "행 그리드 위젯" 파생/판정
//
// 편집형 list 데이터셋(DataSourceDef)을 위젯 트리의 구성원(InputWidget widget:'dataSourceRows')으로
// 승격한다. 이로써 데이터셋이 배치/순서/섹션 포함 대상이 되고(트리 밖 footer 고정 탈피), admin 작성
// 탭 캔버스에도 노출된다. 실제 행 편집 렌더는 host(web)가 주입(RowsCollectionEditor) — 코어는 도메인
// 로직 미포함. 판정(isEditableListDataSource)은 web/admin이 이 모듈을 공유(중복 정의 금지).
//
// 결정: docs/decisions/2026-07-11-writeform-repeat-area-3tier.md Phase 1.

import type {
  BranchWidget,
  ComposerWidget,
  DataSourceDef,
  DataSourceFieldMap,
  GroupWidget,
  InputWidget,
  RepeatWidget,
  ServerSynthesizedCatalogRowKey,
} from '@safetyhub/shared';

/**
 * 편집형(editable) list 데이터 소스만 작성폼 행 그리드 위젯 대상 — editMode 미지정=editable 기본,
 * readonly는 작성폼 위젯을 감춘다. single은 flat 소비라 행 위젯 개념이 없다.
 */
export function isEditableListDataSource(def: DataSourceDef): boolean {
  return def.cardinality === 'list' && (def.editMode ?? 'editable') === 'editable';
}

/** dataSources에서 편집형 list 선언만 선별(선언 순서 유지). */
export function editableListDataSources(
  dataSources: DataSourceDef[] | undefined,
): DataSourceDef[] {
  return (dataSources ?? []).filter(isEditableListDataSource);
}

/**
 * 편집형 list 선언 1개를 행 그리드 InputWidget(widget:'dataSourceRows')으로 변환.
 * bindingKey=def.key(=content 편집 키), label=def.label, dataType='array'.
 * 필드 명세(행 위젯 매핑)는 정본 def.fields에 있으므로 위젯에 복제하지 않는다(렌더 시 def 조회).
 */
export function dataSourceRowsWidget(def: DataSourceDef): InputWidget {
  return {
    kind: 'input',
    id: `dsr-${def.key}`,
    bindingKey: def.key,
    widget: 'dataSourceRows',
    label: def.label,
    dataType: 'array',
  };
}

/**
 * 편집형 list 데이터 소스마다 dataSourceRows 위젯을 파생한다.
 * excludeKeys(이미 파생 풀에 있는 바인딩/dataDef 키)와 충돌하는 def.key는 skip(중복 위젯 방지).
 */
export function deriveDataSourceRowsWidgets(
  dataSources: DataSourceDef[] | undefined,
  excludeKeys: ReadonlySet<string>,
): InputWidget[] {
  const out: InputWidget[] = [];
  const seen = new Set<string>();
  for (const def of editableListDataSources(dataSources)) {
    if (excludeKeys.has(def.key) || seen.has(def.key)) continue;
    seen.add(def.key);
    out.push(dataSourceRowsWidget(def));
  }
  return out;
}

/**
 * 트리에 배치된 dataSourceRows 위젯의 bindingKey 집합(group children 재귀).
 * web footer fallback이 "배치된 편집형 list는 그 위치에서, 미배치만 footer"를 판정하는 데 쓴다.
 */
export function collectPlacedDataSourceRowsKeys(tree: ComposerWidget[]): Set<string> {
  const keys = new Set<string>();
  const walk = (nodes: ComposerWidget[]): void => {
    for (const w of nodes) {
      if (w.kind === 'input') {
        if (w.widget === 'dataSourceRows') keys.add(w.bindingKey);
      } else if (w.kind === 'group') {
        walk(w.children);
      } else if (w.kind === 'branch') {
        // branch: 투명 컨테이너 — 슬롯 children의 dataSourceRows도 그 위치 배치(group 동형). 전 슬롯 하강.
        for (const slot of w.branches) walk(slot.children);
      } else if (w.kind === 'formTable') {
        // 표 반복 행의 sourceKey(편집형 list)는 그 위치에 배치된 것 — footer 중복 렌더 방지.
        for (const row of w.rows) {
          if (row.type === 'repeat') keys.add(row.sourceKey);
        }
      } else if (w.kind === 'repeat') {
        // 반복 위젯의 over(편집형 list)도 그 위치에서 소비 — footer 중복 렌더 방지(위와 동일 사유).
        keys.add(w.over);
      }
    }
  };
  walk(tree);
  return keys;
}

// ============================================================
// 반복 위젯(repeat) 뼈대 자동 생성 + drift(미배치 필드) 대조
// (결정: 2026-07-20-composer-repeat-widget-row-scope §뼈대 생성 규칙 — P2 admin 저작)
// ============================================================

/**
 * 등록형 객체 필드 계약 — widgetKey별 자식 필드 사전(경로·라벨·옵션 소스·숫자 여부).
 * 뼈대 생성기와 admin 행 필드 자식 노출·자동 배선이 이 사전 하나를 공유한다(단일 정본) —
 * 저작자가 자식(빈도/강도/수준)을 행 필드 select에서 바로 고르고, 옵션 소스·숫자 기록은
 * 이 사전에서 자동 배선돼 배관(optionsFrom raw)을 볼 일이 없다.
 */
export interface RegisteredObjectSubfield {
  /** 자식 경로(likelihood 등) — bindingKey는 `<부모필드>.<path>`. */
  path: string;
  /** 사람 라벨('빈도(가능성)'). */
  label: string;
  /**
   * 선택지 소스(select 자식) — 있으면 admin 자동 배선이 그 자식을 select + optionsFrom으로 굽는다.
   * 없는 자식(`improvement-image`의 url·prompt 등 — 부모 위젯이 값을 쓰고 자식은 읽기 표시)은
   * `inputWidget`(기본 'text') 읽기 전용 입력으로 굽는다. 2026-09-14 optional로 넓힘.
   */
  optionsFrom?: { catalogKey: string; valueField: string; labelField: string };
  /** FS=number, TL(level)=미지정(라벨 문자열 계약). 지정 시 admin 자동 배선이 위젯 dataType으로 굽는다. */
  dataType?: 'number' | 'string';
  /** optionsFrom 없는 자식의 배치 위젯 힌트(기본 'text'). 'image'=값이 이미지 URL. */
  inputWidget?: 'text' | 'image';
  /** 이 자식이 속하는 방식 슬롯 — 위험도 생성기 슬롯 배치용(방식 축이 없는 계약은 미지정). */
  methodType?: 'FREQUENCY_SEVERITY' | 'THREE_LEVEL';
}

/**
 * 선택지(옵션 소스) 데이터 레지스트리 — 런타임 주입 rows 소스의 선언 사전(경계 계약).
 * admin '선택지' 편집기가 이 목록으로 사람이 읽는 소스 라벨/필드 라벨을 노출한다(raw 키·경로
 * 은닉). 주입 rows는 카탈로그 목록 밖(서버가 평가기준 선택 시 자동 제공)이라 카탈로그 returns로
 * 필드 라벨을 못 얻으므로 여기서 선언한다. key·기본 필드는 REGISTERED_OBJECT_FIELD_CONTRACTS의
 * optionsFrom과 정합해야 한다(위험도 조립 자동 배선과 같은 소스를 가리킴 — 드리프트 가드 테스트).
 */
export interface InjectedRowSource {
  /**
   * 주입 소스 키(카탈로그 목록 밖 — 예 'risk-likelihood-criteria-rows').
   * shared `SERVER_SYNTHESIZED_CATALOG_ROW_KEYS`에 **타입으로 결속**한다 — 임의 문자열(오타·shared에
   * 없는 키)을 tsc가 막는다. "shared에 있는 키가 여기 빠짐"은 타입으로 못 잡으므로 집합 동등
   * 드리프트 가드 테스트(`repeatSkeleton.test.ts`)가 짝으로 지킨다.
   */
  key: ServerSynthesizedCatalogRowKey;
  /** 사람이 읽는 소스 라벨('기준표 · 빈도(가능성) 단계'). */
  label: string;
  /** 이 rows가 어디서 오는지 안내(조건 데이터 출처·힌트 재사용). */
  providedBy: string;
  /** 소스 행이 제공하는 필드(값/이름 콤보의 한글 라벨 원천). */
  fields: { path: string; label: string; type: 'number' | 'string' }[];
  /** 저장할 값(value) 기본 필드 경로. */
  defaultValueField: string;
  /** 표시할 이름(label) 기본 필드 경로. */
  defaultLabelField: string;
}

/**
 * 위험성평가 기준표 3종(빈도/강도/위험도 수준) — 평가기준 선택 시 서버가 rows로 자동 제공.
 * FS(빈도·강도)는 level(number) 값이 계약(dataType number), TL(위험도 수준)은 label 문자열이
 * 계약(defaultValueField='label'). REGISTERED_OBJECT_FIELD_CONTRACTS['risk-estimation']의
 * optionsFrom과 key·valueField·labelField가 일치한다.
 */
export const INJECTED_ROW_SOURCES: InjectedRowSource[] = [
  {
    key: 'risk-likelihood-criteria-rows',
    label: '기준표 · 빈도(가능성) 단계',
    providedBy: '평가기준 선택 시 서버 자동 제공',
    fields: [
      { path: 'level', label: '단계 값', type: 'number' },
      { path: 'label', label: '단계 이름', type: 'string' },
      // display = `${label} (${level})`(서버 3-g 주입에서 파생) — 구 SMS select 옵션 룩('보통 (2)').
      { path: 'display', label: '단계 이름 (값 병기)', type: 'string' },
    ],
    defaultValueField: 'level',
    defaultLabelField: 'display',
  },
  {
    key: 'risk-severity-criteria-rows',
    label: '기준표 · 강도(중대성) 단계',
    providedBy: '평가기준 선택 시 서버 자동 제공',
    fields: [
      { path: 'level', label: '단계 값', type: 'number' },
      { path: 'label', label: '단계 이름', type: 'string' },
      // display = `${label} (${level})`(서버 3-g 주입에서 파생) — 구 SMS select 옵션 룩('보통 (2)').
      { path: 'display', label: '단계 이름 (값 병기)', type: 'string' },
    ],
    defaultValueField: 'level',
    defaultLabelField: 'display',
  },
  {
    key: 'risk-level-criteria-rows',
    label: '기준표 · 위험도 수준 단계',
    providedBy: '평가기준 선택 시 서버 자동 제공',
    fields: [
      { path: 'level', label: '단계 값', type: 'number' },
      { path: 'label', label: '단계 이름', type: 'string' },
    ],
    // TL은 라벨 문자열이 값 계약(위험도 수준은 '상/중/하' 라벨로 저장) — dataType 미부여.
    defaultValueField: 'label',
    defaultLabelField: 'label',
  },
];

export const REGISTERED_OBJECT_FIELD_CONTRACTS: Record<string, RegisteredObjectSubfield[]> = {
  'risk-estimation': [
    {
      path: 'likelihood',
      label: '빈도 (가능성)',
      methodType: 'FREQUENCY_SEVERITY',
      dataType: 'number',
      optionsFrom: {
        catalogKey: 'risk-likelihood-criteria-rows',
        valueField: 'level',
        // display = `${label} (${level})`(서버 주입 파생) — 구 SMS select 옵션 룩('보통 (2)').
        labelField: 'display',
      },
    },
    {
      path: 'severity',
      label: '강도 (중대성)',
      methodType: 'FREQUENCY_SEVERITY',
      dataType: 'number',
      optionsFrom: {
        catalogKey: 'risk-severity-criteria-rows',
        valueField: 'level',
        labelField: 'display',
      },
    },
    {
      path: 'level',
      label: '위험성 수준',
      methodType: 'THREE_LEVEL',
      optionsFrom: {
        catalogKey: 'risk-level-criteria-rows',
        valueField: 'label',
        labelField: 'label',
      },
    },
  ],
  /**
   * 개선 포인트(사례) AI 이미지(widgetKey 'improvement-image', 결정 2026-09-14 §4) — 값 객체
   * `{ url, prompt?, model?, imageId?, usageCount?, fileId? }`. 부모 위젯(ImprovementImageWidget)이 생성으로
   * 값을 쓰고(유사 검색·재사용은 2026-09-14 제거 — widgets/improvementImageHost.ts 헤더 참조), 본문
   * image 위젯은 `<필드>.url`을 바인딩해 표시한다. 자식은 전부 읽기 표시용(선택지 없음) —
   * imageId·usageCount는 **변환된 구 문서**가 싣고 있는 재사용 사례의 식별/횟수(승인 시 지식 훅이
   * 소비), prompt·model은 새로 생성한 이미지의 근거.
   * `fileId`는 **원장 포인터**(`files` purpose `IMPROVEMENT_IMAGE`, P3-8) — 서버가 생성 시점에 만들고
   * 결재 완료 훅이 `improvement_images.file_id`로 옮긴다(그것이 「채택」의 정의). 저작자가 손으로
   * 채울 값이 아니라 **사슬이 끊기지 않게 계약에 등재**해 둔 것이다.
   */
  'improvement-image': [
    { path: 'url', label: '이미지 URL', dataType: 'string', inputWidget: 'image' },
    { path: 'prompt', label: '생성 프롬프트', dataType: 'string' },
    { path: 'model', label: '생성 모델', dataType: 'string' },
    { path: 'imageId', label: '재사용 사례 ID', dataType: 'string' },
    { path: 'usageCount', label: '재사용 횟수', dataType: 'number' },
    { path: 'fileId', label: '파일 원장 ID', dataType: 'string' },
  ],
};

/**
 * 위험도 필드(widget:'registered' + widgetKey:'risk-estimation') → 평가기준 방식 분기 조립 생성.
 * 뼈대 생성기(반복 배치)가 구형 registered 위젯 대신 이 조립을 자동 생성한다 — 위험도 필드를
 * 선언한 어떤 보고서든 반복 배치만으로 조립이 나온다(신규 생성 경로에서만 registered 은퇴 —
 * 기존 템플릿 렌더 호환 위해 registered 렌더 코드·카탈로그 항목은 잔존).
 *
 * 형상(dev 실물과 동일): group(title=필드 label, titleVariant='label'=plain 라벨 룩) > branch {
 *   slot-0 '빈도·강도' (FREQUENCY_SEVERITY) → group(columns:2) > 빈도 select + 강도 select,
 *   slot-1 '상중하' (THREE_LEVEL) → 위험도 수준 select }.
 * 각 select의 bindingKey·라벨·옵션 소스·dataType은 REGISTERED_OBJECT_FIELD_CONTRACTS['risk-estimation']
 * 사전 소비 — bindingKey는 행 필드 상대 점 경로(`<field>.<sub.path>`), FS는 level 값(number)이 계약이라
 * dataType:'number', TL은 라벨 문자열 계약이라 dataType 없음. collectRepeatPlacedFieldKeys가 점 경로
 * 첫 세그먼트(`<field>`)를 placed로 인정해 drift 오판을 막는다. 슬롯 라벨·조건(방식 분기)은 사전이
 * 아닌 생성기 소유(레이아웃). visibleWhen은 생성하지 않는다(저작자가 추가). editable:false면 select에
 * mode:'readonly'.
 */
function buildRiskEstimationAssembly(
  field: DataSourceFieldMap,
  newId: () => string,
  readonly: boolean,
): GroupWidget {
  const base = field.field;
  const roMode: Pick<InputWidget, 'mode'> = readonly ? { mode: 'readonly' } : {};
  const contract = REGISTERED_OBJECT_FIELD_CONTRACTS['risk-estimation'] ?? [];
  const subSelect = (sub: RegisteredObjectSubfield): InputWidget => {
    const w: InputWidget = {
      kind: 'input',
      id: newId(),
      bindingKey: `${base}.${sub.path}`,
      widget: 'select',
      label: sub.label,
      treeOwned: true,
      ...roMode,
    };
    // 위험도 계약 자식은 전부 select(optionsFrom 보유) — 계약 타입이 optional로 넓혀졌지만
    // (improvement-image 자식은 선택지 없음) 이 조립은 risk-estimation 사전만 읽는다.
    if (sub.optionsFrom) w.optionsFrom = { ...sub.optionsFrom };
    if (sub.dataType) w.dataType = sub.dataType;
    return w;
  };
  const fsGroup: GroupWidget = {
    kind: 'group',
    id: newId(),
    columns: 2,
    children: contract.filter((s) => s.methodType === 'FREQUENCY_SEVERITY').map(subSelect),
  };
  const levelChildren: ComposerWidget[] = contract
    .filter((s) => s.methodType === 'THREE_LEVEL')
    .map(subSelect);
  const branchId = newId();
  const branch: BranchWidget = {
    kind: 'branch',
    id: branchId,
    branches: [
      {
        id: `${branchId}-slot-0`,
        label: '빈도·강도',
        condition: "riskAssessmentMethodType === 'FREQUENCY_SEVERITY'",
        children: [fsGroup],
      },
      {
        id: `${branchId}-slot-1`,
        label: '상중하',
        condition: "riskAssessmentMethodType === 'THREE_LEVEL'",
        children: levelChildren,
      },
    ],
  };
  const group: GroupWidget = {
    kind: 'group',
    id: newId(),
    // titleVariant='label': 조립 제목을 필드 라벨처럼 plain 렌더(회색 헤더 바·테두리 박스 없이) —
    // 구 SMS 위험도 "라벨 + 빈도/강도 select" 룩과 동형(캔버스·실폼 WYSIWYG).
    titleVariant: 'label',
    children: [branch],
  };
  if (field.label) group.title = field.label;
  return group;
}

/**
 * 데이터소스 필드 1개 → 행 템플릿 위젯(뼈대 매핑 정본 — 배치 시 자동 생성과 drift 원클릭
 * 추가가 같은 규칙을 공유).
 *  - hidden 필드는 미생성(null — 행 객체 데이터는 통째 보존되므로 트리 간결 우선, 사용자 확정).
 *  - registered + widgetKey:'risk-estimation' → 평가기준 방식 분기 조립(group+branch+select)으로 전환
 *    (buildRiskEstimationAssembly). 그 외 registered는 기존 registered 위젯 유지.
 *  - text/textarea/number 직결, textarea-list/text-list→동명 WidgetType 직결(P1 편입).
 *  - date/date-range → 동명 WidgetType 직결(달력 입력·기간 입력). date-range는 이 직결 규약 때문에
 *    WidgetType에도 편입돼 있다(shared doc — 행 어휘에만 두면 여기서 타입이 성립하지 않는다).
 *  - image → widget:'image' 직결(읽기 전용 이미지 표시 — 값=URL). image는 mode 무관 항상 표시
 *    전용이라 별도 mode 강제 불요(generic 경로가 그대로 매핑, editable:false면 mode:'readonly'가
 *    붙어도 image 렌더가 무시).
 *  - annotated-image → widget:'annotated-image' 직결(image의 상위 호환 — 값이 객체면 도형 오버레이,
 *    문자열이면 평면 이미지). 표시 전용 규약도 image와 동일.
 *  - signature → widget:'signature' 직결(서명 슬롯 칸 — 표시 전용). 선언의 signerNameField를
 *    위젯에 굽는다(widgetKey와 같은 이유 — 행 안 렌더는 선언을 못 본다).
 *  - readonly **위젯**만 mode:'readonly'. `editable:false`는 굽지 않는다 — 아래 주석 참조.
 *  - bindingKey=행 필드 상대키(field명), label=필드 label. treeOwned(행 스코프 트리 소유물 —
 *    body 파생 대조·클론 제외 규칙의 오판 방지).
 */
export function repeatSkeletonWidgetForField(
  field: DataSourceFieldMap,
  newId: () => string,
): ComposerWidget | null {
  if (field.hidden) return null;
  const fieldWidget = field.widget ?? 'text';
  // 위젯 종류가 'readonly'인 경우만 읽기 전용으로 굽는다.
  //
  // `field.editable === false`는 **일부러 제외한다**(2026-08-12). 그 플래그는 개별 위젯이 없는
  // 자리(행 그리드·formTable 반복 셀 — 매 렌더마다 `field.editable ?? true`를 읽는다)의 축이고,
  // 여기서 위젯을 만드는 순간 편집 여부의 주인은 **그 위젯**으로 넘어간다. 종전처럼 생성 시점
  // 값을 새겨 넣으면 두 축이 갈라진다 — 소스를 고쳐도 이미 배치된 위젯은 따라오지 않고, 저장된
  // mode가 "사람이 지정한 것"인지 "생성 때 구워진 것"인지 구분할 수도 없다.
  //
  // 결과적으로 자동 배치는 **편집 가능이 기본**이 된다. 작성폼은 API로 받은 값을 초깃값 삼아
  // 고쳐서 최종 문서를 만드는 자리라 그게 맞고, 잠가야 하는 값(문서 정체성·집계 산출)은 배치
  // 후 위젯 속성에서 지정한다. 결정 기록: docs/decisions/2026-08-12-writeform-editable-axis.md
  const readonly = fieldWidget === 'readonly';
  if (fieldWidget === 'registered' && field.widgetKey === 'risk-estimation') {
    return buildRiskEstimationAssembly(field, newId, readonly);
  }
  const widget: InputWidget = {
    kind: 'input',
    id: newId(),
    bindingKey: field.field,
    widget: fieldWidget === 'readonly' ? 'text' : fieldWidget,
    treeOwned: true,
  };
  if (field.label) widget.label = field.label;
  if (fieldWidget === 'registered' && field.widgetKey) widget.widgetKey = field.widgetKey;
  // 서명 슬롯의 서명자 이름 배선 — widgetKey와 같은 이유로 굽는다(행 안 렌더는 선언을 못 본다).
  if (fieldWidget === 'signature' && field.signerNameField) {
    widget.signerNameField = field.signerNameField;
  }
  if (readonly) widget.mode = 'readonly';
  return widget;
}

/** def.fields → 행 템플릿 children 1:1 뼈대(선언 순서 유지, hidden 제외). */
export function buildRepeatSkeletonWidgets(
  def: DataSourceDef,
  newId: () => string,
): ComposerWidget[] {
  const out: ComposerWidget[] = [];
  for (const f of def.fields) {
    const w = repeatSkeletonWidgetForField(f, newId);
    if (w) out.push(w);
  }
  return out;
}

/**
 * 편집형 조립 데이터소스 1개를 뼈대 children과 함께 반복 위젯으로 생성 — admin 배치 채널 정본
 * (팔레트 '+위젯' 반복 추가 / 미배치 풀 dataSourceRows 배치의 repeat 대체 — 강제 단일화).
 */
export function createRepeatWidgetForDataSource(
  def: DataSourceDef,
  newId: () => string,
): RepeatWidget {
  return {
    kind: 'repeat',
    id: newId(),
    over: def.key,
    children: buildRepeatSkeletonWidgets(def, newId),
  };
}

/**
 * 반복 위젯 행 템플릿에 배치된 행 필드 키 집합 — drift(미배치 필드) 대조용.
 * 하강: group children 재귀 + branch 슬롯 children + formTable 고정 셀 위젯(행 템플릿 안 자유 조립 대상 전부).
 * 미하강: modalTrigger children(모달 스코프 — 행 필드 아님) / 중첩 repeat(다른 over 스코프).
 *
 * 점 경로 bindingKey(중첩 조립, 예 `riskEstimation.likelihood`)는 그 자체 + **첫 세그먼트**
 * (`riskEstimation`)도 placed로 인정한다 — 중첩 위젯들이 그 객체 필드를 커버하므로, def.fields의
 * `riskEstimation` 필드가 미배치로 오판되지 않게(drift 원클릭 추가 중복 방지).
 */
export function collectRepeatPlacedFieldKeys(children: ComposerWidget[]): Set<string> {
  const keys = new Set<string>();
  const addKey = (bindingKey: string): void => {
    keys.add(bindingKey);
    const dot = bindingKey.indexOf('.');
    if (dot > 0) keys.add(bindingKey.slice(0, dot));
  };
  const walk = (nodes: ComposerWidget[]): void => {
    for (const w of nodes) {
      if (w.kind === 'input') {
        addKey(w.bindingKey);
      } else if (w.kind === 'group') {
        walk(w.children);
      } else if (w.kind === 'branch') {
        for (const slot of w.branches) walk(slot.children);
      } else if (w.kind === 'formTable') {
        for (const row of w.rows) {
          if (row.type !== 'fixed') continue;
          for (const cell of row.cells) if (cell.widget) addKey(cell.widget.bindingKey);
        }
      }
    }
  };
  walk(children);
  return keys;
}

/**
 * 뼈대 생성 후 필드 선언이 추가돼 행 템플릿에 미배치된 def.fields — "미배치 필드 N개" 안내 +
 * 원클릭 추가(placed copy drift 해소 — placed/orphan 대조 규약 동형). hidden 필드는 집계 제외.
 */
export function repeatMissingFields(
  widget: RepeatWidget,
  def: DataSourceDef,
): DataSourceFieldMap[] {
  const placed = collectRepeatPlacedFieldKeys(widget.children);
  return def.fields.filter((f) => !f.hidden && !placed.has(f.field));
}

/**
 * id 위젯을 감싼 반복(repeat) 컨테이너(가장 안쪽 값 스코프 기준) — 속성 패널의 행 스코프 판정용(P4a).
 * 반복 children의 값 스코프는 행 객체(bindingKey=행 필드 상대키)라, body 전제 축(writeFormFields
 * dataType 파생·prefill·풀 복귀)과 참조 후보(visibleWhen 등)를 행 스코프로 갈라야 한다.
 * 스코프는 **innermost 승자**: modalTrigger children은 모달 스코프(modalValues)로 격리되므로
 * 진입 시 repeat 컨텍스트를 리셋(모달 안 위젯은 반복 안이어도 행 스코프 아님). 반대로 반복 안
 * 모달의 바깥(행 직속) 위젯은 repeat을 반환. formTable 고정 셀 위젯도 현재 스코프 소속으로 하강.
 * 미발견/본문/모달 스코프면 null.
 */
export function findEnclosingRepeatWidget(
  tree: ComposerWidget[],
  id: string,
): RepeatWidget | null {
  let result: RepeatWidget | null = null;
  let found = false;
  const walk = (nodes: ComposerWidget[], repeat: RepeatWidget | null): void => {
    for (const n of nodes) {
      if (found) return;
      if (n.id === id) {
        result = repeat;
        found = true;
        return;
      }
      if (n.kind === 'group') walk(n.children, repeat);
      else if (n.kind === 'repeat') walk(n.children, n);
      // branch: 투명 컨테이너 — 감싸는 repeat 컨텍스트를 그대로 전파(group 동형). 전 슬롯 하강.
      else if (n.kind === 'branch') for (const slot of n.branches) walk(slot.children, repeat);
      else if (n.kind === 'modalTrigger') walk(n.children, null);
      else if (n.kind === 'formTable') {
        for (const row of n.rows) {
          if (row.type !== 'fixed') continue;
          for (const cell of row.cells) {
            if (cell.widget && cell.widget.id === id) {
              result = repeat;
              found = true;
              return;
            }
          }
        }
      }
    }
  };
  walk(tree, null);
  return result;
}
